import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./SearchBar.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

/**
 * An interactive Search Bar component with a customizable placeholder text.
 * Logs inputs if minimum character prop is satisfied with an optional delay.
 * Width of Search Bar determined by container width.
 * Search delay is measured in ms.
 */

export function SearchBar({
  placeholderText,
  onInput,
  searchDelay,
  minChar,
  className,
  btn,
}) {
  const [input, setInput] = useState("");
  const [minCharError, setMinCharError] = useState(false);

  useMemo(() => {
    if (input.length >= minChar && !btn) {
      const timer = setTimeout(() => {
        onInput(input);
      }, searchDelay);

      return () => clearTimeout(timer);
    }
  }, [input, onInput, searchDelay, minChar, btn]);

  const handleClick = () => {
    setMinCharError(false);
    if (input.length >= minChar) {
      onInput(input);
    } else {
      setMinCharError(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleClick();
    }
  };

  return (
    <>
      <div className={classNames(styles.container, className)}>
        <div className={styles.row}>
          <FontAwesomeIcon icon={faSearch} className={styles.magnifyingGlass} />
          <input
            type="text"
            name="search"
            id="search"
            placeholder={placeholderText}
            className={styles.searchBar}
            onKeyDown={handleKeyDown}
            onChange={(key) => setInput(key.target.value)}
          />
          {btn ? (
            <input
              type="submit"
              name="submit"
              id="submit"
              value="SEARCH"
              onClick={handleClick}
              className={styles.searchBtn}
            />
          ) : null}
        </div>
        {minCharError ? (
          <div className={classNames(styles.row, styles.error)}>
            Please enter a search query greater than {minChar} characters.
          </div>
        ) : null}
      </div>
    </>
  );
}

SearchBar.propTypes = {
  // placeholder text
  placeholderText: PropTypes.string,

  // function that fires when user types and minChar/searchDelay is satisfied
  onInput: PropTypes.func,

  // number of ms before onInput is called
  searchDelay: PropTypes.number,

  // minimum characters needed to fire onInput
  minChar: PropTypes.number,

  // Changes the style of the search bar
  className: PropTypes.string,

  // Decides if a btn is displayed to search or if search happens, onInput
  btn: PropTypes.bool,
};

SearchBar.defaultProps = {
  placeholderText: "",
  onInput: (e) => console.log(e),
  searchDelay: 0,
  minChar: 0,
  className: "",
  btn: false,
};
